import { z } from "zod";
import { IsoTimestamp, MatchId, NightId, PlayerId } from "./ids.js";

// ─────────────────────────────────────────────────────────────────────────────
// LOOP 4B — THE TOURNAMENT ENGINE (SEASON ONE v1.1 insertion; DUGOUT S3).
// Single-elimination v1. Bracket matches ride the SAME match machine as open
// play (scheduled flow: match_paired → PAIRED → READY_CHECK → …) — one
// integrity system, two formats. Every repair arrives player-side through
// normal game states; a repaired bracket never looks "admin-edited".
// ─────────────────────────────────────────────────────────────────────────────

// Machine: DRAFT → PUBLISHED → RUNNING → COMPLETE · DRAFT/PUBLISHED → CANCELLED
export const TournamentState = z.enum(["DRAFT", "PUBLISHED", "RUNNING", "COMPLETE", "CANCELLED"]);
export type TournamentState = z.infer<typeof TournamentState>;

export const TournamentSize = z.union([z.literal(4), z.literal(8), z.literal(16)]);
export type TournamentSize = z.infer<typeof TournamentSize>;

// Entry: free | coins (BLG coin sink) | token (season reward — behind
// feature.entry_tokens until the token ledger exists; see DRIFT-009).
export const EntryType = z.enum(["free", "coins", "token"]);
export type EntryType = z.infer<typeof EntryType>;

export const EntryConfig = z.object({
  type: EntryType,
  cost_coins: z.number().int().min(0), // 0 unless type === "coins"
});
export type EntryConfig = z.infer<typeof EntryConfig>;

// Prize pool: house-seeded, in minor units (cents). Split = integer percents
// over final ranks (1st, 2nd, SF losers…), sum 100. Payout floors each share;
// remainder cents go to 1st — the queue must reconcile to the cent.
export const PrizeConfig = z.object({
  pool_minor: z.number().int().min(0),
  split_percents: z.array(z.number().int().min(0).max(100)).min(1).max(8),
});
export type PrizeConfig = z.infer<typeof PrizeConfig>;

export const Tournament = z.object({
  id: z.string().uuid(),
  night_id: NightId,
  name: z.string().min(1).max(60),
  state: TournamentState,
  size: TournamentSize,
  entry: EntryConfig,
  prize: PrizeConfig,
  /** Minutes a round's ready-check window stays open before repairs kick in. */
  round_minutes: z.number().int().min(5).max(120),
  starts_at: IsoTimestamp,
  created_at: IsoTimestamp,
  entered: z.number().int().min(0), // live entry count (standby excluded)
});
export type Tournament = z.infer<typeof Tournament>;

export const TournamentEntry = z.object({
  tournament_id: z.string().uuid(),
  player_id: PlayerId,
  seed: z.number().int().min(1).nullable(), // assigned at start; null while entered
  standby: z.boolean(), // beyond capacity — replace-player pool (S3 repair)
  entered_at: IsoTimestamp,
});
export type TournamentEntry = z.infer<typeof TournamentEntry>;

// One bracket position. round 1 = first round; slot is 0-based within the
// round. match_id links the REAL match row once the pairing is created — the
// bracket never carries its own result truth, it reads the match machine's.
export const BracketSlotState = z.enum([
  "PENDING", // waiting on feeder results
  "SCHEDULED", // both players known, match created (PAIRED/READY_CHECK/…)
  "DONE", // winner decided (COMPLETE or WALKOVER)
  "VOID", // voided pairing awaiting redraw (repair)
  "BYE", // odd fill — player advances without playing (undersized start)
]);
export type BracketSlotState = z.infer<typeof BracketSlotState>;

export const BracketSlot = z.object({
  tournament_id: z.string().uuid(),
  round: z.number().int().min(1),
  slot: z.number().int().min(0),
  home_id: PlayerId.nullable(),
  away_id: PlayerId.nullable(),
  match_id: MatchId.nullable(),
  winner_id: PlayerId.nullable(),
  state: BracketSlotState,
});
export type BracketSlot = z.infer<typeof BracketSlot>;

// Player + DUGOUT bracket read (the DUGOUT enriches nodes with match status
// chips via the engine's queue reads; the player view is this envelope).
export const BracketView = z.object({
  tournament: Tournament,
  slots: z.array(BracketSlot),
  my_entry: TournamentEntry.nullable(), // null for spectators / DUGOUT
  champion_id: PlayerId.nullable(),
});
export type BracketView = z.infer<typeof BracketView>;

// ── DUGOUT S3 requests ───────────────────────────────────────────────────────
export const CreateTournamentRequest = z.object({
  night_code: z.string().min(1),
  name: z.string().min(1).max(60),
  size: TournamentSize,
  entry: EntryConfig,
  prize: PrizeConfig,
  round_minutes: z.number().int().min(5).max(120).default(20),
  starts_at: IsoTimestamp,
});
export type CreateTournamentRequest = z.infer<typeof CreateTournamentRequest>;

// Repair actions (S3): every one reason-coded; every one lands player-side as
// a normal game state (walkover/void), never as an edit.
export const RepairAction = z.enum([
  "award_walkover", // no-show protocol → READY_CHECK → WALKOVER on the live match
  "extend_round", // pushes the round clock (no match transition)
  "replace_player", // standby swap, pre-round-start only (PENDING/SCHEDULED unplayed)
  "void_redraw", // voids the pairing's match; slot returns to SCHEDULED with a fresh match
]);
export type RepairAction = z.infer<typeof RepairAction>;

export const RepairRequest = z.object({
  round: z.number().int().min(1),
  slot: z.number().int().min(0),
  action: RepairAction,
  reason_code: z.string().min(1),
  /** award_walkover: the player who turned up. replace_player: the standby coming in. */
  player_id: PlayerId.optional(),
  /** replace_player: the participant being pulled (which side the standby takes). */
  outgoing_player_id: PlayerId.optional(),
  /** extend_round: minutes to add. */
  extend_minutes: z.number().int().min(1).max(60).optional(),
});
export type RepairRequest = z.infer<typeof RepairRequest>;

// ── Payouts (→ THE VAULT) ────────────────────────────────────────────────────
export const TournamentPayout = z.object({
  tournament_id: z.string().uuid(),
  player_id: PlayerId,
  final_rank: z.number().int().min(1),
  amount_minor: z.number().int().min(0),
  vault_item_id: z.string().uuid().nullable(), // set once queued into THE VAULT
  queued_at: IsoTimestamp.nullable(),
});
export type TournamentPayout = z.infer<typeof TournamentPayout>;

export const PayoutQueueView = z.object({
  tournament_id: z.string().uuid(),
  pool_minor: z.number().int(),
  payouts: z.array(TournamentPayout),
  /** Must equal pool_minor when the split covers the pool — the to-the-cent check. */
  total_queued_minor: z.number().int(),
});
export type PayoutQueueView = z.infer<typeof PayoutQueueView>;

// ── Player requests ──────────────────────────────────────────────────────────
export const EnterTournamentRequest = z.object({
  tournament_id: z.string().uuid(),
});
export type EnterTournamentRequest = z.infer<typeof EnterTournamentRequest>;

export const EnterTournamentResponse = z.object({
  entered: z.boolean(),
  standby: z.boolean(),
  coins_after: z.number().int().nullable(), // null on free/token entries
});
export type EnterTournamentResponse = z.infer<typeof EnterTournamentResponse>;

export const TournamentListView = z.object({
  tournaments: z.array(Tournament),
});
export type TournamentListView = z.infer<typeof TournamentListView>;

/**
 * SE bracket math: rounds = log2(size); round r has size / 2^r slots.
 * Feeders of (round r, slot s) are (r-1, 2s) and (r-1, 2s+1).
 */
export function roundsFor(size: TournamentSize): number {
  return Math.log2(size);
}
export function slotsInRound(size: TournamentSize, round: number): number {
  return size / 2 ** round;
}
